import { useCallback, useEffect, useState } from 'react';
import type { Role, Technician, User } from '../../data/types';
import { api, NetworkError } from '../../lib/api';
import { useSyncStatus } from '../../sync/status';

/**
 * User accounts are managed online only: they are security records the server must validate immediately
 * (unique usernames, password rules, keeping an administrator), so they are never queued offline.
 */
export function useAccounts(enabled: boolean) {
  const { connection } = useSyncStatus();
  const [accounts, setAccounts] = useState<User[]>([]);
  const [error, setError] = useState('');
  const reload = useCallback(async () => {
    if (!enabled) return;
    try { setAccounts((await api<{ users: User[] }>('/api/users')).users); setError(''); }
    catch (failure) { setError(failure instanceof NetworkError ? 'User accounts are available when this device is online.' : (failure as Error).message); }
  }, [enabled]);
  useEffect(() => { reload(); }, [reload, connection]);
  return { accounts, error, reload };
}

export interface AccountInput { displayName: string; username: string; email: string; password: string; role: Role; active: boolean; technicianId?: string; technicianEmail?: string }

export async function createAccount(input: AccountInput) {
  return (await api<{ user: User }>('/api/users', { method: 'POST', body: input })).user;
}

export async function updateAccount(account: User, input: Partial<AccountInput>) {
  return (await api<{ user: User }>(`/api/users/${encodeURIComponent(account.id)}`, { method: 'PUT', body: { displayName: account.displayName, username: account.username, email: account.email || '', role: account.role, active: account.active, technicianId: account.technicianId || undefined, technicianEmail: account.technicianEmail || undefined, ...input, password: input.password || '' } })).user;
}

export function findTechnicianAccount(technician: Pick<Technician, 'id' | 'email' | 'name'>, accounts: User[]) {
  return accounts.find((account) => account.technicianId && account.technicianId === technician.id)
    || accounts.find((account) => !account.technicianId && account.role === 'tech' && ((technician.email && account.technicianEmail === technician.email) || account.displayName === technician.name));
}

export function temporaryPassword() {
  const bytes = crypto.getRandomValues(new Uint8Array(9));
  return `PCS-${Array.from(bytes, (byte) => 'abcdefghjkmnpqrstuvwxyz23456789'[byte % 31]).join('')}!`;
}
